import DOMPurify from "dompurify";
import React, {
    ReactElement,
} from "react";
import {
    BaseEditor, Descendant, Node, Text,
} from "slate";
import { ReactEditor } from "slate-react";
// eslint-disable-next-line import/no-extraneous-dependencies
import he from "he";
import { BlockHandler } from "./blocks";
import {
    AnyChild, BlockDeserializeSettings, BlockSettings, EditorSettings,
    InlineHandler, StateElement, StyleType, TextElement,
} from "./blocks/base";

declare module "slate" {
    interface CustomTypes {
        Editor: BaseEditor & ReactEditor
        Element: StateElement;
        Text: TextElement;
    }

}

export interface BlockElementProps {
    attributes: React.HTMLProps<HTMLParagraphElement>;
    children: ReactElement;
    element: StateElement;
    editorSettings: EditorSettings;
}

export function BlockElement({
    attributes, children, element, editorSettings,
}: BlockElementProps): ReactElement {
    let child = children;
    const options = element as StateElement;
    const handler = BlockHandler.get(options.type);
    if (handler) {
        child = handler.toBlock(child, options[options.type as unknown as keyof StateElement] as BlockSettings, editorSettings);
    }

    // eslint-disable-next-line react/jsx-props-no-spreading
    return (<p {...attributes}>{child}</p>);
}

export interface LeafProps {
    attributes: React.HTMLProps<HTMLSpanElement>;
    children: ReactElement;
    leaf: TextElement;
    editorSettings: EditorSettings;
}

export function Leaf({
    attributes, children, leaf, editorSettings,
}: LeafProps): ReactElement {
    let child = children;
    const options = leaf as TextElement;

    Object.entries(options).forEach(([type, settings]) => {
        if (type === "text") {
            return;
        }
        const handler = InlineHandler.get(type as StyleType);
        if (handler) {
            child = handler.toBlock(child, settings, editorSettings);
        }
    });

    // eslint-disable-next-line react/jsx-props-no-spreading
    return <span {...attributes}>{child}</span>;
}

// eslint-disable-next-line no-undef
export function deserialize(el: ChildNode, settings: BlockDeserializeSettings): AnyChild {
    if (el.nodeType === 3) {
        const content = el.textContent?.replace(/([\s\n]+$)|(^[\s\n]+)/g, " ") ?? "";
        return { text: content } as TextElement;
    }
    if (el.nodeType !== 1) {
        return undefined;
    }

    let children = Array.from(el.childNodes).map(item => deserialize(item, settings));

    if (children.length === 0) {
        children = [{ text: "" } as TextElement];
    }

    children = children.flatten();

    if (el.nodeName) {
        const inlineType = InlineHandler.getByNodeType(el.nodeName);
        if (inlineType) {
            return children.map(child => (Text.isText(child)
                ? inlineType.deserialize(child as TextElement, el as Element, settings)
                : child) as Descendant);
        }

        const nodeType = BlockHandler.getByNodeType(el.nodeName);
        if (nodeType) {
            return nodeType.deserialize(children, el as Element);
        }
        return children as Descendant[];
    }

    const content = el.textContent;
    return { text: content ?? "" } as TextElement;
}

export function deserializeFromHtml(html: string, settings: BlockDeserializeSettings): Descendant[] {
    const document = new window.DOMParser().parseFromString(html, "text/html");
    let result = deserialize(document.body, settings);
    if (Text.isText(result) || Text.isTextList(result)) {
        result = Array.isArray(result) ? result : [result];
        result = (result.isNotEmpty ? result : [{ text: "" }]) as TextElement[];
        return [BlockHandler.getByNodeType("P").deserialize(result, undefined, settings) as Descendant];
    }
    if (Array.isArray(result)) {
        const inter = result.map(row => (Text.isText(row)
            ? BlockHandler.getByNodeType("P").deserialize([row as TextElement | StateElement], undefined, settings)
            : row)) as Descendant[];
        return inter;
    }
    return [result] as StateElement[];
}

export function serializeNode(node: Descendant): string {
    if (Text.isText(node)) {
        let children = DOMPurify.sanitize(he.encode(Node.string(node)));
        Object.entries(node).forEach(([key, settings]) => {
            if (key === "text") {
                return;
            }
            const handler = InlineHandler.get(key as StyleType);
            if (handler) {
                children = handler.serialize(children, settings);
            }
        });
        return children;
    }

    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    const children = serialize(node.children);

    const handler = BlockHandler.get(node.type);

    return handler ? handler.serialize(children, node[node.type as unknown as keyof StateElement]) : children;
}
export function serialize(nodes: Descendant[]): string {
    return nodes.map(child => serializeNode(child)).join("");
}
